// The Filter widget's metric-range slider column — an IMPERATIVE ISLAND, ported from
// js/renderers-filter.js (buildSliderRows / wireSliderRow / sliderRowHtml /
// ensureCollapseDefaults). Same rationale as the chip-bar hybrid: the drag/DOM here is
// self-contained legacy code, so it's copied verbatim into a TS module that FilterModule
// mounts into a React-owned (but React-childless) host, driven by an `onChange`→React-recompute
// bridge. The pure geometry (metricDomain, value↔px↔pct, applyHandle) lives in data/filter.ts
// and is unit-tested; this file is the DOM wiring (untested, like the chip-bar — no jsdom).
//
// It READS mod._filterMetricRanges (never resets it) so undo/preset restore of a saved range
// set redraws the handles in place; FilterModule owns clearing ranges on a season/mode change
// (a new real domain invalidates a stale narrowed range).

import type { ProtoMetricCategory, ProtoPlayerMetric } from "@catbento/shared";
import type { RawPlayerRecord } from "../data/snapshot.js";
import {
  metricDomain,
  valueToPct,
  pxToValue,
  isDefaultRange,
  applyHandle,
  type FilterMetricRange,
  type FilterMetricRanges,
} from "../data/filter.js";
import { formatValueForMode, type KitMetric } from "../engines/stats-filter-kit.js";
import { humanizeSeasonCode } from "./league-table-view.js";

type Players = Record<string, RawPlayerRecord>;

// The `_`-state the island reads/writes on the module element (captured by saveState/undo).
interface FilterSliderState {
  _filterMetricRanges: FilterMetricRanges;
  _filterCollapsedCategories: Record<string, boolean>;
}

export interface FilterSliderIsland {
  /** Redraw every slider from the current mod._filterMetricRanges (after an external clear,
   *  e.g. Reset all) without rebuilding the DOM. */
  redrawAll(): void;
}

export interface BuildFilterSlidersOptions {
  rows: HTMLElement;
  players: Players;
  categories: readonly ProtoMetricCategory[];
  seasonCode: string;
  mode: string;
  mod: HTMLElement;
  onChange: () => void;
}

function sliderRowHtml(m: ProtoPlayerMetric): string {
  return `
    <div class="filter-metric-row" data-key="${m.key}">
      <div class="filter-metric-row-top">
        <span class="filter-metric-label">${m.shortName || m.name}</span>
        <span class="filter-metric-readout"></span>
        <span class="filter-metric-reset" title="Reset to full range">✕</span>
      </div>
      <div class="filter-range-track-wrap">
        <div class="filter-range-track"></div>
        <div class="filter-range-fill"></div>
        <div class="filter-range-handle" data-handle="min" tabindex="0"></div>
        <div class="filter-range-handle" data-handle="max" tabindex="0"></div>
      </div>
    </div>
  `;
}

// Any category not yet toggled defaults to collapsed — with 162 sliders across 5 categories,
// opening on 5 headers reads far better than a wall of sliders (renderers-filter.js:268).
function ensureCollapseDefaults(state: FilterSliderState, categories: readonly ProtoMetricCategory[]): void {
  if (!state._filterCollapsedCategories) state._filterCollapsedCategories = {};
  for (const cat of categories) {
    if (!(cat.id in state._filterCollapsedCategories)) state._filterCollapsedCategories[cat.id] = true;
  }
}

// Wires one metric's always-visible dual-handle slider. Returns its draw() so Reset all can
// redraw every handle without a rebuild. renderers-filter.js:157.
function wireSliderRow(
  opts: BuildFilterSlidersOptions,
  state: FilterSliderState,
  m: ProtoPlayerMetric,
): (() => void) | null {
  const { rows, players, seasonCode, mode, onChange } = opts;
  const row = rows.querySelector<HTMLElement>(`.filter-metric-row[data-key="${CSS.escape(m.key)}"]`);
  if (!row) return null;
  const domain = metricDomain(players, m, seasonCode, mode);
  const trackWrap = row.querySelector<HTMLElement>(".filter-range-track-wrap")!;
  const fillEl = row.querySelector<HTMLElement>(".filter-range-fill")!;
  const minHandle = row.querySelector<HTMLElement>('[data-handle="min"]')!;
  const maxHandle = row.querySelector<HTMLElement>('[data-handle="max"]')!;
  const readoutEl = row.querySelector<HTMLElement>(".filter-metric-readout")!;
  const resetEl = row.querySelector<HTMLElement>(".filter-metric-reset")!;

  // A degenerate (max ≤ min) domain means no real data this season — an honest no-data row.
  if (!(domain.max > domain.min)) {
    row.classList.add("filter-metric-row-empty");
    readoutEl.textContent = `No data in ${humanizeSeasonCode(seasonCode)}`;
    trackWrap.style.display = "none";
    resetEl.style.display = "none";
    return null;
  }

  const currentRange = (): FilterMetricRange => state._filterMetricRanges[m.key] ?? { min: domain.min, max: domain.max };

  function draw(): void {
    const r = currentRange();
    const loPct = valueToPct(r.min, domain);
    const hiPct = valueToPct(r.max, domain);
    minHandle.style.left = loPct + "%";
    maxHandle.style.left = hiPct + "%";
    fillEl.style.left = loPct + "%";
    fillEl.style.width = Math.max(hiPct - loPct, 0) + "%";
    const isDefault = isDefaultRange(r, domain);
    readoutEl.textContent = isDefault
      ? `0 – ${formatValueForMode(domain.max, m as KitMetric, mode)}`
      : `${formatValueForMode(r.min, m as KitMetric, mode)} – ${formatValueForMode(r.max, m as KitMetric, mode)}`;
    resetEl.style.display = isDefault ? "none" : "";
  }
  draw();

  function setValue(handle: "min" | "max", val: number): void {
    const next = applyHandle(currentRange(), handle, val);
    // A range back at the full domain applies no constraint → drop the key (keeps the sparse
    // convention useFilterResults + the population count rely on). renderers-filter.js:201.
    if (isDefaultRange(next, domain)) delete state._filterMetricRanges[m.key];
    else state._filterMetricRanges[m.key] = next;
    draw();
  }

  // One recompute per animation frame while dragging, not one per mousemove (renderers-filter.js:210).
  let rafPending = false;
  function scheduleUpdate(): void {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(() => {
      rafPending = false;
      onChange();
    });
  }

  function startDrag(handle: "min" | "max"): void {
    function onMove(e: MouseEvent | TouchEvent): void {
      const clientX = "touches" in e ? e.touches[0]!.clientX : e.clientX;
      const rect = trackWrap.getBoundingClientRect();
      setValue(handle, pxToValue(clientX - rect.left, rect.width, domain));
      scheduleUpdate();
    }
    function onUp(): void {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onUp);
      onChange();
    }
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("touchmove", onMove);
    document.addEventListener("touchend", onUp);
  }

  minHandle.addEventListener("mousedown", (e) => { e.preventDefault(); e.stopPropagation(); startDrag("min"); });
  maxHandle.addEventListener("mousedown", (e) => { e.preventDefault(); e.stopPropagation(); startDrag("max"); });
  minHandle.addEventListener("touchstart", (e) => { e.preventDefault(); e.stopPropagation(); startDrag("min"); }, { passive: false });
  maxHandle.addEventListener("touchstart", (e) => { e.preventDefault(); e.stopPropagation(); startDrag("max"); }, { passive: false });
  resetEl.onclick = (e) => { e.stopPropagation(); delete state._filterMetricRanges[m.key]; draw(); onChange(); };

  return draw;
}

/**
 * Build the full 5-category / ~162-metric slider skeleton into `rows` (a React-owned, otherwise
 * empty host), grouped under collapsible category headers, and wire every slider. Reads the
 * current mod._filterMetricRanges for initial handle positions (never resets it — the caller
 * owns that). renderers-filter.js:285.
 */
export function buildFilterSliders(opts: BuildFilterSlidersOptions): FilterSliderIsland {
  const { rows, categories, mod } = opts;
  const state = mod as unknown as FilterSliderState;
  ensureCollapseDefaults(state, categories);

  rows.innerHTML = categories
    .map(
      (cat) => `
      <div class="filter-category-group${state._filterCollapsedCategories[cat.id] ? " collapsed" : ""}" data-category-id="${cat.id}">
        <div class="filter-category-label">
          <span class="filter-category-chevron">▾</span>
          ${cat.name}
        </div>
        ${cat.subcategories
          .map((sub) => {
            const skipLabel = cat.subcategories.length === 1 && sub.name === cat.name;
            return `
            <div class="stat-group">
              ${skipLabel ? "" : `<div class="stat-group-label">${sub.name}</div>`}
              ${sub.playerMetrics.map(sliderRowHtml).join("")}
            </div>
          `;
          })
          .join("")}
      </div>
    `,
    )
    .join("");

  const drawFns: Array<() => void> = [];
  for (const cat of categories) {
    const groupEl = rows.querySelector<HTMLElement>(`.filter-category-group[data-category-id="${CSS.escape(cat.id)}"]`);
    const labelEl = groupEl?.querySelector<HTMLElement>(".filter-category-label");
    if (labelEl && groupEl) {
      // Collapse/expand is a plain DOM class + state toggle — deliberately NOT a rebuild, so
      // the live sliders and their drag state survive it (renderers-filter.js:313).
      labelEl.onclick = () => {
        const collapsed = !state._filterCollapsedCategories[cat.id];
        state._filterCollapsedCategories[cat.id] = collapsed;
        groupEl.classList.toggle("collapsed", collapsed);
      };
    }
    for (const sub of cat.subcategories) {
      for (const m of sub.playerMetrics) {
        const draw = wireSliderRow(opts, state, m);
        if (draw) drawFns.push(draw);
      }
    }
  }

  return { redrawAll: () => drawFns.forEach((fn) => fn()) };
}
